import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';

// SERVICES

import { AngularEditorConfig } from '@kolkov/angular-editor';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  concat,
  from,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  startWith,
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  tap,
  takeUntil,
  take,
  filter,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import {
  ProductTypeConstant,
  ProductTypes,
} from '@app/core/_constant/product-type.constants';
import { BomService } from '@app/core/bom/services/bom.service';
import { IBom } from '@app/core/bom/store/bom.model';
import { FactoryService } from '@app/core/factories';
import { IRfqProductSubstitution } from '@app/core/inventory/store/inventory.model';
import { LocationService } from '@app/core/locations/services/location.service';
import { LotTrackingService } from '@app/core/lot-tracking/services/lot-tracking.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { RFQ, RfqService } from '@app/core/quotes';
import { DefaultReceivingBin } from '@app/core/quotes/store/quotes.model';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { ShippingCarrierService } from '@app/core/shipping-carriers/services/shipping-carrier.service';
import { UnitClassService } from '@app/core/unit-measurement/services/unit-class/unit-class.service';
import { UnitMeasureService } from '@app/core/unit-measurement/services/unit-measure/unit-measure.service';
import { environment } from '@app/env-service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import {
  UnitClassInterface,
  UnitMeasureInterface,
} from '@app/modules/unit-measurement/interfaces/unit-measurement.interface';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { FulfillmentPriorityListApiService } from 'v2/modules/shared/services/api/fulfillment-priority-list-api.service';
import { QueryBuilderUtils } from 'v2/modules/shared/util/query-builder.utils';
import { ECustomFieldObject } from 'v2/modules/custom-field/interfaces/custom-field.interface';
import { CreateClientRfqVariantComponent } from '../create-client-rfq-variant/create-client-rfq-variant.component';

@Component({
  selector: 'cnc-create-client-rfq',
  templateUrl: './create-client-rfq.component.html',
  styleUrls: ['./create-client-rfq.component.scss'],
})
export class CreateClientRfqComponent implements OnInit, OnDestroy {
  url :string = environment.url;
  eCustomFieldObject = ECustomFieldObject;
  pageTitle :string = 'Create SKU';
  searchFailed :boolean = false;
  searching :boolean = false;
  activeTab :number = 0;

  showFormError :boolean;
  submitted :boolean = false;
  loading :boolean = false;
  uploadedFilesCnt :number = 0;
  quoteForm :UntypedFormGroup;
  filesToUpload :any[] = [];
  fileRemarks :any[] = [];
  rfqAttachments :any[] = [];
  attachmentsToDelete :any[] = [];
  productVariants :any[] = [];
  rfq :RFQ;
  factories :any[] = [];
  selectedCategory :any;

  tags :any = [];
  selectedTags :any = [];
  productSubstitutions :IRfqProductSubstitution[] = [];

  quote_expires :boolean = false;

  baseUnitClass$ :Observable<UnitClassInterface>;
  baseUnitClass :UnitClassInterface;

  unitMeasures$ :Observable<UnitMeasureInterface[]>;
  unitMeasures :UnitMeasureInterface[] = [];

  unitClasses$ :Observable<UnitClassInterface[]>;
  unitClasses :UnitClassInterface[] = [];

  unitClassInput$ = new Subject<string>();
  unitMeasureInput$ = new Subject<string>();

  bomInput$ = new Subject<string>();
  bomLoading :boolean = false;
  boms :IBom[] = [];

  queryParams :{ [param :string] :string | string[] };

  generatedSku$ :Observable<any>;
  generatingSku$ :Observable<boolean>;

  gettingDefaultWarehouseLocations$ :Observable<any>;
  defaultWarehouseLocations$ :Observable<any>;
  defaultWarehouseLocationInput$ = new Subject<string>();

  defaultReceivingBin :DefaultReceivingBin;

  productTypes = ProductTypes;
  productTypeSelected = ProductTypes.PRODUCT;

  lotTrackingEnabled :boolean = false;

  featureFlag = EFeatureFlag;

  fulfillmentPriorityListInput$ = new Subject<string>();
  fulfillmentPriorityListLoading :boolean = false;
  fulfillmentPriorityList :any[] = [];

  @Input() product_type :number;
  @Input() parentSku :any;
  @Input() consolidatedRfq :RFQ = null;
  @Input() sku :any = null;
  @Input() product_name :any = null;

  config :AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    placeholder: 'Enter text here...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    sanitize: false,
    customClasses: [
      {
        name: 'quote',
        class: 'quote',
      },
      {
        name: 'redText',
        class: 'redText',
      },
      {
        name: 'titleText',
        class: 'titleText',
        tag: 'h1',
      },
    ],
  };

  private onDestroy$ = new Subject();
  canReadUnitCost :boolean = false;

  constructor(
    public activeModal :NgbActiveModal,
    // private clientSvc: ClientService,
    public productCategoriesSvc :CategoryService,
    public rfqService :RfqService,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private toastr :ToastrService,
    private modalSvc :NgbModal,
    public settingsSvc :SettingsService,
    public translate :TranslateService,
    public factoryService :FactoryService,
    public shippingCarrierSvc :ShippingCarrierService,
    public tagSvc :ProductTagService,
    private unitClassService :UnitClassService,
    private unitMeasureService :UnitMeasureService,
    private locationService :LocationService,
    private lotTrackingService :LotTrackingService,
    private bomService :BomService,
    private ft :FeatureToggleService,
    private permissionService :NgxPermissionsService,
    private fulfillmentPriorityListApiService :FulfillmentPriorityListApiService,
  ) {}

  ngOnInit() {
    this.checkLotTrackingIfEnabled();
    this.generateSku();
    this.loadBaseUnitClass();
    this.getDataFromService();

    this.defaultWarehouseLocationInputUpdated();
    this.getInventorySetting().subscribe();

    this.loadUnitClasses();

    this.unitClassInputUpdated();
    this.unitMeasureInputUpdated();

    this.bomInputUpdated();
    this.fulfillmentPriorityListInputUpdated();

    this.tagSvc.getTags({ parent_only: true }, null);
    this.tagSvc.formattedRecord$.subscribe((tags :any) => {
      this.tags = tags;
    });

    this.pageTitle = 'Create ' + (this.product_type ? 'RFQ' : 'SKU');
    const dateNow = new Date();
    this.quoteForm = this.fb.group({
      product_name: [
        this.product_name !== null ? this.product_name : '',
        Validators.required,
      ],
      product_type_id: [this.product_type || 2, Validators.required],

      product_dept_id: [null],
      is_palletized: [null],
      cartons_per_pallet: [null],
      shipment_value: [null],
      units_per_carton: [null],
      units_per_pallet: [null],

      pallet_length: [null],
      pallet_width: [null],
      pallet_height: [null],

      shipping_length: [null],
      shipping_width: [null],
      shipping_height: [null],

      product_length: [null],
      product_width: [null],
      product_height: [null],

      box_length: [null],
      box_width: [null],
      box_height: [null],
      carton_length: [null],
      carton_width: [null],
      carton_height: [null],
      expires_at: [''],

      sub_category_id: [0],
      product_category_id: ['', [Validators.required, Validators.min(1)]],
      product_description: [''],
      units_qty_annual: [0],
      units_qty_first: [0],

      reorder_level: [0],
      qty_limit_notify: [0],
      oss_limit_notify: [0],

      volume_first_order: [1],
      volume_annual_order: [1],
      target_price: [1],

      will_purchase_sample: [''],
      is_product_in_market: [''],
      internal_sku: [this.sku !== null ? this.sku : '', Validators.required],
      upc: [''],

      rfq_quote_type: [16, Validators.required],

      units_qty_annual_type: [''],
      address_not_known: [false],
      changes_to_make: [''],
      development_status: [''],
      units_qty_moq: [''],
      brand_product: [''],
      artwork_available: [''],
      product_has_variants: [''],

      pr_labeling_stickers: [''],
      pr_custom_printing: [''],
      pr_bundling: [''],
      pr_various_prep: [''],
      pr_carton_arrangement: [''],
      pr_same_as_link: [''],
      pr_generic_quote: [''],

      pr_labeling_stickers_text: [''],
      pr_custom_printing_text: [''],
      pr_bundling_text: [''],
      pr_various_prep_text: [''],
      pr_carton_arrangement_text: [''],
      pr_generic_quote_text: [''],

      od_sample_to_match: [''],
      od_sold_on_amazon: [''],
      od_pro_product_pictures: [''],
      od_need_help_in_prototype: [''],
      od_need_graphic_design: [''],
      od_special_certificates: [''],
      od_info_details: [''],

      destination_address: [''],
      destination_city: [''],
      destination_state: [''],
      destination_zip: [''],
      has_prototype: [''],
      has_packing_req: [''],
      other_details: [''],
      default_photo: ['0'],

      fileremarks: this.fb.array([]),
      shippingWeights: this.fb.array([]),
      productWeights: this.fb.array([]),
      dimensionalWeights: this.fb.array([]),
      boxWeights: this.fb.array([]),
      cartonWeights: this.fb.array([]),
      palletWeights: this.fb.array([]),

      material: this.fb.array([]),
      custom_fields: this.fb.array([]),
      product_links: this.fb.array([]),

      labor_cost: [0],
      package_cost: [0],
      default_shipping: [0],
      efficiency_score: [0],
      preferred_vendor_id: [0],

      internal_use_only: [false],
      photo: [''],
      product_design: [''],

      retail_price: [0],
      wholesale_price: [0],
      unitCost: [
        this.rfq && this.rfq.base_price ? this.rfq.base_price.weighted : 0,
      ],

      lead_time: [0],
      basePrice: [[]],

      mirror_inventory_from_parent: [false],

      product_tags: [[]],

      unit_class_id: [null, Validators.required],
      default_selling_unit_id: [null, Validators.required],
      default_purchase_unit_id: [null, Validators.required],

      default_receiving_bin_id: [null],

      bom_ids: [[]],

      kitItems: this.fb.array([]),
      type: [this.productTypeSelected],

      lot_tracking: [false],
      sellable: [true],
      customFieldValues: [''],
      tax_category: [''],
      fulfillment_priority_list_id: [null],
      is_fulfill_separately: [false],
    });

    this.factoryService.factory$.subscribe((factories :any) => {
      this.factories = factories;
    });

    if (this.rfq === undefined) {
      if (this.parentSku) {
        this.quoteForm.patchValue({
          unit_class_id: this.parentSku.unit_class_id,
        });
        this.quoteForm.patchValue({
          default_selling_unit_id: this.parentSku.default_selling_unit_id,
        });
        this.quoteForm.patchValue({
          default_purchase_unit_id: this.parentSku.default_purchase_unit_id,
        });
        this.quoteForm.patchValue({
          default_receiving_bin_id: this.parentSku.default_receiving_bin_id,
        });
        this.loadUnitMeasures();
      } else {
        this.getBaseUnitClass().subscribe();
      }
    } else {
      this.quoteForm.patchValue({
        unit_class_id: this.rfq.unit_class_id,
      });
      this.quoteForm.patchValue({
        default_selling_unit_id: this.rfq.default_selling_unit_id,
      });
      this.quoteForm.patchValue({
        default_purchase_unit_id: this.rfq.default_purchase_unit_id,
      });
      this.loadUnitMeasures();
    }

    if (this.rfq && this.rfq.id) {
      this.isRfqCategoryIdExist();

      const rfq :any = Object.assign({}, this.rfq);
      this.patchForm(rfq);
    } else {
      this.addMaterials('material');
      this.addLink('product_links');
      this.addWeight('shippingWeights');
      this.addWeight('dimensionalWeights');
      this.addWeight('productWeights');
      this.addWeight('boxWeights');
      this.addWeight('cartonWeights');
      this.addWeight('palletWeights');

      if (this.parentSku) {
        this.pageTitle =
          'Create new VARIANT SKU for ' + this.parentSku.internal_sku;
        const rfq :any = Object.assign({}, this.parentSku);
        rfq.internal_sku = this.getIncrementedVariantSku(this.parentSku);
        delete rfq.id;

        rfq.variant_parent_id = this.parentSku.id;

        this.patchForm(rfq);
      } else {
        this.addField('custom_fields');
      }
    }

    if (this.consolidatedRfq) {
      const rfq :RFQ = Object.assign({}, this.consolidatedRfq);
      this.patchForm(rfq);
    }

    this.appendWarehouseLocations();
    this.autopopulateGeneratedSku();

    this.permissionService
      .hasPermission('skuUnitCost.read')
      .then((hasPermission) => {
        this.canReadUnitCost = hasPermission;
      });
  }

  private getInventorySetting() :Observable<any> {
    return this.settingsSvc.settings$.pipe(
      tap((inventorySettings) => {
        if (inventorySettings && inventorySettings.inventory_settings) {
          this.getWarehouseInfo(
            inventorySettings.inventory_settings.warehouse_id,
          ).subscribe();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getWarehouseInfo(warehouseId :number) :Observable<any> {
    return this.locationService.getById(warehouseId).pipe(
      tap((warehouse :any) => {
        this.getDefaultReceivingLocation(
          warehouse.id,
          warehouse.default_receiving_location,
        ).subscribe();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  getDefaultReceivingLocation(
    id :number,
    defaultReceivingLocationId :number,
  ) :Observable<any> {
    return this.locationService
      .getAll({
        all: 1,
        parent_id: id,
        defaultReceivingBinId: defaultReceivingLocationId,
      })
      .pipe(
        tap((resp) => {
          if (
            !this.rfq?.default_receiving_bin &&
            !this.parentSku?.default_receiving_bin
          ) {
            this.defaultReceivingBin = resp.find(
              (row) => row.id == defaultReceivingLocationId,
            );
            this.quoteForm.patchValue({
              default_receiving_bin_id: this.defaultReceivingBin?.id,
            });
            this.appendWarehouseLocations();
          }
        }),
        takeUntil(this.onDestroy$),
      );
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private resetSubCategoryValue() :void {
    this.quoteForm.patchValue({
      sub_category_id: '0',
    });
  }

  private setSubCategory() :void {
    this.resetSubCategoryValue();
    this.getSubCategories();
  }

  getSubCategories() {
    this.selectedCategory = {};

    const category :any = this.productCategoriesSvc.getProductCategoryById(
      this.quoteForm.getRawValue().product_category_id,
    );
    if (!category || category.length == 0) {
      setTimeout(() => {
        this.getSubCategories();
      }, 500);
    }
    if (category && category.length > 0) {
      this.selectedCategory = category[0];
    }
  }

  patchForm(rfq :any) {
    delete rfq.fileremarks;
    delete rfq.shippingWeights;
    delete rfq.dimensionalWeights;
    delete rfq.productWeights;
    delete rfq.boxWeights;
    delete rfq.cartonWeights;
    delete rfq.palletWeights;
    delete rfq.material;
    delete rfq.custom_fields;
    delete rfq.product_links;

    if (rfq.lot_tracking && !this.parentSku) {
      this.quoteForm.controls.lot_tracking.disable();
    }

    let product_tag_ids :any = [];
    if (rfq.tags && rfq.tags.length > 0) {
      product_tag_ids = rfq.tags.map((e) => e.tag_id);
    }

    let bomIds :any = null;
    if (rfq.bom_rfqs && rfq.bom_rfqs.length > 0) {
      bomIds = rfq.bom_rfqs.map((bomRfqs) => bomRfqs.bom_id);
    }

    if (rfq.variant_parent_id === 0 && !!rfq.product_variants) {
      const productVariantsData =
        typeof rfq.product_variants === 'string'
          ? JSON.parse(rfq.product_variants)
          : this.rfq.product_variants;
      this.productVariants = productVariantsData.filter((productVariant) => {
        return typeof productVariant !== 'string';
      });
    }

    rfq.preferred_vendor_id = parseInt(rfq.preferred_vendor_id.toString());
    if (rfq.id) {
      this.pageTitle =
        rfq.variant_parent_id > 0
          ? 'Update VARIANT SKU'
          : 'Update ' + (this.product_type ? 'RFQ' : 'SKU');
      this.typeControl.disable();
    }
    if (this.consolidatedRfq) {
      this.pageTitle = 'Create New Master SKU';
    }

    this.quoteForm.patchValue({ ...rfq, product_tags: product_tag_ids });

    this.quoteForm.patchValue({
      bom_ids: bomIds,
    });

    this.quote_expires = rfq.expires_at != null ? true : false;

    if (rfq.variant_parent_id > 0) {
      this.quoteForm.patchValue({
        default_shipping: rfq?.default_shipping,
      });
      this.quoteForm.patchValue({
        product_category_id: rfq?.product_category_id,
      });
      this.quoteForm.patchValue({
        sub_category_id: rfq?.sub_category_id,
      });
      this.quoteForm.patchValue({
        internal_use_only: rfq?.internal_use_only,
      });
      this.quoteForm.patchValue({
        product_category: rfq?.product_category,
      });

      this.quoteForm.controls.product_category_id.disable();
      this.quoteForm.controls.sub_category_id.disable();
      this.quoteForm.controls.default_shipping.disable();
      this.quoteForm.controls.internal_use_only.disable();
    }

    if (rfq.upc) {
      this.quoteForm.patchValue({
        upc: rfq.upc,
      });
    }

    if (rfq.product_dept_id == 0) {
      this.quoteForm.get('product_dept_id').patchValue(null);
    }

    if (rfq.product_category_id > 0) {
      this.getSubCategories();
    }

    if (rfq && rfq.base_price) {
      this.quoteForm.patchValue({
        unitCost: rfq.base_price.weighted,
        basePrice: rfq.base_price,
      });
    }

    // custom fields
    if (rfq.other_details && rfq.other_details.length > 0) {
      try {
        rfq.other_details = JSON.parse(rfq.other_details);
      } catch (e) {}
      rfq.other_details.forEach((row) => {
        if (row.l !== '' || row.v !== '') {
          this.addField(row);
        }
      });

      this.addField('custom_links');
    } else {
      this.addField('custom_links');
    }
    // product links
    if (rfq.product_link && rfq.product_link.length > 0) {
      try {
        rfq.product_link = JSON.parse(rfq.product_link);
      } catch (e) {}
      rfq.product_link.forEach((row) => {
        this.addLink('product_links', row);
      });
    } else {
      this.addLink('product_links');
    }
    // product materials
    if (rfq.materials && rfq.materials.length > 0) {
      try {
        rfq.materials = JSON.parse(rfq.materials);
      } catch (e) {}
      rfq.materials.forEach((row) => {
        this.addMaterials('material', row);
      });
    } else {
      this.addMaterials('material');
    }
    // weights
    if (rfq.shipping_weight === '[]') {
      this.addWeight('shippingWeights');
    } else if (rfq.shipping_weight && rfq.shipping_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.shipping_weight)) ===
          '[object Array]'
        ) {
          rfq.shipping_weight = JSON.parse(rfq.shipping_weight);
        } else {
          var holder = [
            {
              wv: rfq.shipping_weight,
              mu: 'oz',
            },
          ];
          rfq.shipping_weight = holder;
        }
      } catch (e) {}
      rfq.shipping_weight.forEach((row) => {
        this.addWeight('shippingWeights', row);
      });
    } else {
      this.addWeight('shippingWeights');
    }
    if (rfq.product_weight === '[]') {
      this.addWeight('productWeights');
    } else if (rfq.product_weight && rfq.product_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.product_weight)) ===
          '[object Array]'
        ) {
          rfq.product_weight = JSON.parse(rfq.product_weight);
        } else {
          var holder = [
            {
              wv: rfq.product_weight,
              mu: 'oz',
            },
          ];
          rfq.product_weight = holder;
        }
      } catch (e) {}
      rfq.product_weight.forEach((row) => {
        this.addWeight('productWeights', row);
      });
    } else {
      this.addWeight('productWeights');
    }
    if (rfq.dimensional_weight === '[]') {
      this.addWeight('dimensionalWeights');
    } else if (rfq.dimensional_weight && rfq.dimensional_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.dimensional_weight)) ===
          '[object Array]'
        ) {
          rfq.dimensional_weight = JSON.parse(rfq.dimensional_weight);
        } else {
          var holder = [
            {
              wv: rfq.dimensional_weight,
              mu: 'oz',
            },
          ];
          rfq.dimensional_weight = holder;
        }
      } catch (e) {}
      rfq.dimensional_weight.forEach((row) => {
        this.addWeight('dimensionalWeights', row);
      });
    } else {
      this.addWeight('dimensionalWeights');
    }
    if (rfq.box_weight === '[]') {
      this.addWeight('boxWeights');
    } else if (rfq.box_weight && rfq.box_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.box_weight)) ===
          '[object Array]'
        ) {
          rfq.box_weight = JSON.parse(rfq.box_weight);
        } else {
          var holder = [
            {
              wv: rfq.box_weight,
              mu: 'oz',
            },
          ];
          rfq.box_weight = holder;
        }
      } catch (e) {}
      rfq.box_weight.forEach((row) => {
        this.addWeight('boxWeights', row);
      });
    } else {
      this.addWeight('boxWeights');
    }
    if (rfq.carton_weight === '[]') {
      this.addWeight('cartonWeights');
    } else if (rfq.carton_weight && rfq.carton_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.carton_weight)) ===
          '[object Array]'
        ) {
          rfq.carton_weight = JSON.parse(rfq.carton_weight);
        } else {
          var holder = [
            {
              wv: rfq.carton_weight,
              mu: 'oz',
            },
          ];
          rfq.carton_weight = holder;
        }
      } catch (e) {}
      rfq.carton_weight.forEach((row) => {
        this.addWeight('cartonWeights', row);
      });
    } else {
      this.addWeight('cartonWeights');
    }
    if (rfq.pallet_weight === '[]') {
      this.addWeight('palletWeights');
    } else if (rfq.pallet_weight && rfq.pallet_weight.length > 0) {
      try {
        if (
          Object.prototype.toString.call(JSON.parse(rfq.pallet_weight)) ===
          '[object Array]'
        ) {
          rfq.pallet_weight = JSON.parse(rfq.pallet_weight);
        } else {
          var holder = [
            {
              wv: rfq.pallet_weight,
              mu: 'oz',
            },
          ];
          rfq.pallet_weight = holder;
        }
      } catch (e) {}
      rfq.pallet_weight.forEach((row) => {
        this.addWeight('palletWeights', row);
      });
    } else {
      this.addWeight('palletWeights');
    }
    this.onProductTypeChange();
  }

  addWeight(key :string, weight ?:any) {
    const weights = this.quoteForm.get(key) as UntypedFormArray;
    const weightFc :any = this.fb.group({
      wv: [weight ? weight.wv : 0, Validators.required],
      mu: [weight ? weight.mu : 'lbs', Validators.required],
    });
    weights.push(weightFc);
  }

  removeWeight(key :string, idx) {
    const weights = this.quoteForm.get(key) as UntypedFormArray;
    weights.removeAt(idx);
  }

  addField(field ?:any) {
    const fields = this.quoteForm.get('custom_fields') as UntypedFormArray;
    const fieldFc :any = this.fb.group({
      l: [field && field.l ? field.l : ''],
      v: [field && field.v ? field.v : ''],
    });
    fields.push(fieldFc);
  }

  removeField(idx) {
    const links = this.quoteForm.get('custom_fields') as UntypedFormArray;
    links.removeAt(idx);
  }

  addLink(key :string, link ?:any) {
    const links = this.quoteForm.get(key) as UntypedFormArray;
    const linkFc :any = this.fb.group({
      lv: [link ? link.lv : ''],
    });
    links.push(linkFc);
  }

  removeLink(key :string, idx) {
    const links = this.quoteForm.get(key) as UntypedFormArray;
    links.removeAt(idx);
  }

  addMaterials(key :string, mat ?:any) {
    const mats = this.quoteForm.get(key) as UntypedFormArray;
    const materialFc :any = this.fb.group({
      mv: [mat ? mat.mv : ' '],
    });
    mats.push(materialFc);
  }

  removeMaterials(key :string, idx) {
    const mats = this.quoteForm.get(key) as UntypedFormArray;
    mats.removeAt(idx);
  }

  setRfq(id :number) {
    const rfq = this.rfqService.getActiveRfq();
    if (!rfq || !rfq.id || rfq.id != id) {
      const kani = this;
      setTimeout(() => {
        kani.setRfq(id);
      }, 1000);

      return;
    }

    this.rfq = rfq;
    this.quoteForm.patchValue(rfq);

    this.rfqAttachments = rfq.rfq_attachments;
    const fileremarks = this.quoteForm.get('fileremarks') as UntypedFormArray;
    rfq.rfq_attachments.forEach((file :any, idx) => {
      if (this.rfq.photo == file.filename) {
        this.quoteForm.patchValue({ default_photo: idx.toString() });
      }
      if (file.thumburl && file.thumburl != '') {
        this.filesToUpload.push({
          previewSrc: this.url + '/file?f=' + file.thumburl,
        });
      } else {
        if (file.filetype && file.filetype.indexOf('image') >= 0) {
          this.filesToUpload.push({
            previewSrc: this.url + '/file?f=' + file.filename,
          });
        } else {
          this.filesToUpload.push({
            filename: file.fileurl || 'File',
          });
        }
      }

      fileremarks.push(this.fb.control(file.remarks));
    });
  }

  validateUomKitItems(kitItems :any) :boolean {
    let valid = true;
    kitItems.forEach((kitItem, idx) => {
      if (kitItem.productId && !kitItem.uomId) {
        valid = false;

        return;
      }
    });

    return valid;
  }

  submit() {
    const data :any = Object.assign({}, this.quoteForm.getRawValue());

    this.submitted = true;

    if (!this.quoteForm.valid) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FORM_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    if (data.type === this.productTypes.KIT && !this.hasKitItems) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
        text: 'Please select at least 1 kit item.',
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });

      return;
    }

    if (data.type === this.productTypes.KIT && this.hasKitItems) {
      const isValidKitItems = this.validateUomKitItems(data.kitItems);
      if (!isValidKitItems) {
        Swal.fire({
          title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
          text: 'Please fill in all the required fields in kit items',
          type: 'error',
          confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
        });

        return;
      }

      const notSellableSku = [];
      data.kitItems.forEach((kitItem) => {
        if (kitItem?.sku?.sellable === 0) {
          notSellableSku.push(kitItem?.sku?.sku);
        }
      });
      if (notSellableSku?.length > 0) {
        Swal.fire({
          title: this.translate.instant('MESSAGES.FORM_ERROR.TITLE'),
          text:
            'These PRODUCT SKUs are not sellable: ' +
            notSellableSku.join(', ') +
            '. KIT components should be composed of sellable SKUs only.',
          type: 'error',
          confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
        });

        return;
      }
    }

    const fd = new FormData();
    fd.append('business_account_id', '0');
    fd.append('added_by', '0');

    const prodType = this.settingsSvc.productTypes.find(
      (row) => row.id == data.product_type_id,
    ).text;
    const prodDepartment = this.settingsSvc.productDepartments.find(
      (row) => row.id == data.product_dept_id,
    );

    let prodDepartmentText = '';
    let prodDepartmentID = '0';

    if (prodDepartment == undefined) {
      prodDepartmentText = '';
      prodDepartmentID = '0';
    } else {
      prodDepartmentText = prodDepartment.text;
      prodDepartmentID = (
        data.product_dept_id ? data.product_dept_id : 0
      ).toString();
    }

    fd.append('product_dept_id', prodDepartmentID);
    fd.append('product_dept', prodDepartmentText);

    fd.append('custom_fields', JSON.stringify(data.custom_fields));
    fd.append('product_link', JSON.stringify(data.product_links));
    fd.append('materials', JSON.stringify(data.material));

    fd.append('shipping_weight', JSON.stringify(data.shippingWeights));
    fd.append('dimensional_weight', JSON.stringify(data.dimensionalWeights));
    fd.append('labor_cost', data.labor_cost.toString());
    fd.append('package_cost', data.package_cost.toString());
    fd.append('default_shipping', data.default_shipping.toString());
    fd.append('efficiency_score', data.efficiency_score.toString());

    fd.append('product_name', data.product_name);
    fd.append('product_type_id', data.product_type_id.toString());

    fd.append('product_type', prodType);
    fd.append('internal_sku', data.internal_sku);
    fd.append('upc', data.upc ? data.upc : '');
    fd.append('product_description', data.product_description);
    fd.append('product_category_id', data.product_category_id);
    fd.append('units_qty_annual', data.units_qty_annual);
    fd.append('units_qty_first', data.units_qty_first);
    fd.append('target_price', data.target_price);
    fd.append(
      'will_purchase_sample',
      (data.will_purchase_sample ? 1 : 0).toString(),
    );
    fd.append('is_product_in_market', data.is_product_in_market);
    fd.append('destination_address', data.destination_address);
    fd.append('destination_city', data.destination_city);
    fd.append('destination_state', data.destination_state);
    fd.append('destination_zip', data.destination_zip);
    fd.append('has_prototype', (data.has_prototype ? 1 : 0).toString());
    fd.append('has_packing_req', (data.has_packing_req ? 1 : 0).toString());
    fd.append('other_details', JSON.stringify(data.custom_fields));
    fd.append('default_photo', data.default_photo);
    fd.append('expires_at', this.quote_expires ? data.expires_at : '');

    fd.append('reorder_level', data.reorder_level.toString());
    fd.append('qty_limit_notify', data.qty_limit_notify.toString());
    fd.append('oss_limit_notify', data.oss_limit_notify.toString());
    fd.append('tax_category', data.tax_category);
    fd.append('fulfillment_priority_list_id', data.fulfillment_priority_list_id);
    fd.append('is_fulfill_separately', data.is_fulfill_separately);

    fd.append(
      'unit_class_id',
      (data.unit_class_id ? data.unit_class_id : 0).toString(),
    );

    fd.append(
      'default_selling_unit_id',
      (data.default_selling_unit_id ? data.default_selling_unit_id : 0).toString(),
    );

    fd.append(
      'default_purchase_unit_id',
      (data.default_purchase_unit_id
        ? data.default_purchase_unit_id
        : 0
      ).toString(),
    );

    fd.append(
      'is_palletized',
      (data.is_palletized ? data.is_palletized : 0).toString(),
    );
    fd.append(
      'cartons_per_pallet',
      (data.cartons_per_pallet ? data.cartons_per_pallet : 0).toString(),
    );
    fd.append(
      'shipment_value',
      (data.shipment_value ? data.shipment_value : 0).toString(),
    );

    fd.append('product_weight', JSON.stringify(data.productWeights));

    fd.append('box_weight', JSON.stringify(data.boxWeights));
    fd.append('box_length', (data.box_length ? data.box_length : 0).toString());
    fd.append('box_width', (data.box_width ? data.box_width : 0).toString());
    fd.append('box_height', (data.box_height ? data.box_height : 0).toString());

    fd.append('carton_weight', JSON.stringify(data.cartonWeights));
    fd.append(
      'carton_length',
      (data.carton_length ? data.carton_length : 0).toString(),
    );
    fd.append(
      'carton_width',
      (data.carton_width ? data.carton_width : 0).toString(),
    );
    fd.append(
      'carton_height',
      (data.carton_height ? data.carton_height : 0).toString(),
    );

    fd.append('pallet_weight', JSON.stringify(data.palletWeights));
    fd.append(
      'pallet_length',
      (data.pallet_length ? data.pallet_length : 0).toString(),
    );
    fd.append(
      'pallet_width',
      (data.pallet_width ? data.pallet_width : 0).toString(),
    );
    fd.append(
      'pallet_height',
      (data.pallet_height ? data.pallet_height : 0).toString(),
    );

    fd.append(
      'shipping_length',
      (data.shipping_length ? data.shipping_length : 0).toString(),
    );
    fd.append(
      'shipping_width',
      (data.shipping_width ? data.shipping_width : 0).toString(),
    );
    fd.append(
      'shipping_height',
      (data.shipping_height ? data.shipping_height : 0).toString(),
    );

    fd.append(
      'product_length',
      (data.product_length ? data.product_length : 0).toString(),
    );
    fd.append(
      'product_width',
      (data.product_width ? data.product_width : 0).toString(),
    );
    fd.append(
      'product_height',
      (data.product_height ? data.product_height : 0).toString(),
    );

    fd.append(
      'units_per_carton',
      data.units_per_carton ? data.units_per_carton : '',
    );
    fd.append(
      'units_per_pallet',
      data.units_per_pallet ? data.units_per_pallet : '',
    );

    fd.append('rfq_quote_type', data.rfq_quote_type);

    fd.append('volume_first_order', data.volume_first_order);
    fd.append('volume_annual_order', data.volume_annual_order);

    fd.append(
      'pr_labeling_stickers',
      (data.pr_labeling_stickers ? 1 : 0).toString(),
    );
    fd.append('pr_custom_printing', (data.pr_custom_printing ? 1 : 0).toString());
    fd.append('pr_bundling', (data.pr_bundling ? 1 : 0).toString());
    fd.append('pr_various_prep', (data.pr_various_prep ? 1 : 0).toString());
    fd.append(
      'pr_carton_arrangement',
      (data.pr_carton_arrangement ? 1 : 0).toString(),
    );
    fd.append('pr_same_as_link', (data.pr_same_as_link ? 1 : 0).toString());
    fd.append('pr_generic_quote', (data.pr_generic_quote ? 1 : 0).toString());

    fd.append(
      'pr_labeling_stickers_text',
      data.pr_labeling_stickers_text ? data.pr_labeling_stickers_text : '',
    );
    fd.append(
      'pr_custom_printing_text',
      data.pr_custom_printing_text ? data.pr_custom_printing_text : '',
    );
    fd.append(
      'pr_bundling_text',
      data.pr_bundling_text ? data.pr_bundling_text : '',
    );
    fd.append(
      'pr_various_prep_text',
      data.pr_various_prep_text ? data.pr_various_prep_text : '',
    );
    fd.append(
      'pr_carton_arrangement_text',
      data.pr_carton_arrangement_text ? data.pr_carton_arrangement_text : '',
    );
    fd.append(
      'pr_generic_quote_text',
      data.pr_generic_quote_text ? data.pr_generic_quote_text : '',
    );

    fd.append(
      'od_sample_to_match',
      (data.od_sample_to_match ? data.od_sample_to_match : 0).toString(),
    );
    fd.append(
      'od_sold_on_amazon',
      (data.od_sold_on_amazon ? data.od_sold_on_amazon : 0).toString(),
    );
    fd.append(
      'od_pro_product_pictures',
      (data.od_pro_product_pictures ? data.od_pro_product_pictures : 0).toString(),
    );
    fd.append(
      'od_need_help_in_prototype',
      (data.od_need_help_in_prototype
        ? data.od_need_help_in_prototype
        : 0
      ).toString(),
    );
    fd.append(
      'od_need_graphic_design',
      (data.od_need_graphic_design ? data.od_need_graphic_design : 0).toString(),
    );
    fd.append(
      'od_special_certificates',
      (data.od_special_certificates ? data.od_special_certificates : 0).toString(),
    );
    fd.append('od_info_details', data.od_info_details ? data.od_info_details : '');

    fd.append(
      'units_qty_annual_type',
      (data.units_qty_annual_type ? 1 : 0).toString(),
    );
    fd.append('address_not_known', (data.address_not_known ? 1 : 0).toString());
    fd.append('changes_to_make', data.changes_to_make ? data.changes_to_make : '');
    fd.append(
      'development_status',
      data.development_status ? data.development_status : '',
    );
    fd.append('units_qty_moq', (data.units_qty_moq ? 1 : 0).toString());
    fd.append(
      'brand_product',
      (data.brand_product ? data.brand_product : 0).toString(),
    );
    fd.append(
      'artwork_available',
      (data.artwork_available ? data.artwork_available : 0).toString(),
    );
    fd.append(
      'product_has_variants',
      (data.product_has_variants ? 1 : 0).toString(),
    );

    if (this.rfq?.variant_parent_id === 0 || !this.rfq?.id) {
      fd.append('product_variants', JSON.stringify(this.productVariants));
    }
    fd.append('preferred_vendor_id', data.preferred_vendor_id.toString());

    fd.append('retail_price', data.retail_price);
    fd.append('wholesale_price', data.wholesale_price);
    fd.append('unitCost', data.unitCost);

    if (
      this.ft.featureEnabled(this.featureFlag.ADD_RFQ_LEAD_TIME) &&
      this.rfq?.id
    ) {
      fd.append('lead_time', data.lead_time);
    }
    fd.append('basePrice', JSON.stringify(data.basePrice));

    fd.append('product_tags', data.product_tags);

    fd.append('internal_use_only', (data.internal_use_only ? 1 : 0).toString());
    fd.append(
      'mirror_inventory_from_parent',
      (data.mirror_inventory_from_parent ? 1 : 0).toString(),
    );
    fd.append('default_receiving_bin_id', data.default_receiving_bin_id);

    if (this.parentSku && this.parentSku.id > 0) {
      fd.append('variant_parent_id', this.parentSku.id.toString());
    }

    if (this.selectedCategory && this.selectedCategory.id) {
      fd.append('product_category', this.selectedCategory.product_category);
      if (data.sub_category_id && data.sub_category_id > 0) {
        fd.append('sub_category_id', data.sub_category_id.toString());
        const subCat :any = this.selectedCategory.sub_categories.find(
          (row :any) => row.id == data.sub_category_id,
        );
        if (subCat) {
          fd.append('sub_category', subCat.sub_category);
        }
      }
    }

    fd.append('lot_tracking', data.lot_tracking ? '1' : '0');
    fd.append('customFieldValues', data.customFieldValues || '[]');

    if (data.bom_ids) {
      fd.append('bom_ids', data.bom_ids);
    }

    if (data.type) {
      fd.append('type', data.type);
    }

    if (data.type === this.productTypes.KIT) {
      data.kitItems.forEach((item) => {
        delete item.sku;
      });

      fd.append('kitItems', JSON.stringify(data.kitItems));

      data.sellable = true;
    }
    fd.append('sellable', (+data.sellable).toString());
    const productSubstitutions = [];

    this.productSubstitutions.forEach((item :IRfqProductSubstitution) => {
      const mappedItem = {
        id: item.id,
        priority: item.priority,
        substituteProductId: item.substituteProductId,
      };
      productSubstitutions.push(mappedItem);
    });
    fd.append('productSubstitutions', JSON.stringify(productSubstitutions));

    if (data.photo) {
      fd.append('photo', data.photo);
    }
    if (data.product_design) {
      fd.append('product_design', data.product_design);
    }

    const rfq :any = this.rfq;

    if (rfq != undefined && rfq.variant_parent_id != undefined) {
      if (rfq.variant_parent_id > 0 && rfq.parent) {
        fd.append('product_category', rfq.parent.product_category);
        fd.append('product_category_id', rfq.parent.product_category_id);
        fd.append('sub_category', rfq.parent.sub_category);
        fd.append('sub_category_id', rfq.parent.sub_category_id);
        fd.append('internal_use_only', rfq.parent.internal_use_only);
        fd.append('default_shipping', rfq.parent.default_shipping);
      }
    }

    this.loading = true;

    const fileremarks = this.quoteForm.getRawValue().fileremarks;
    let swalTitle = '';
    let swalText = '';

    if (fileremarks.length > 0) {
      swalTitle = 'File Upload';
      swalText = 'Uploading file attachments, please wait...';
    } else {
      if (this.rfq && this.rfq.id) {
        swalTitle = 'Updating SKU';
      } else {
        swalTitle = 'Creating SKU';
      }
      swalText = 'Saving changes, please wait...';
    }

    Swal.fire({
      title: swalTitle,
      text: swalText,
      backdrop: false,
      allowEscapeKey: false,
    });
    Swal.showLoading();

    if (this.rfq && this.rfq.id) {
      fd.append(
        'existing_file_count',
        (this.rfqAttachments.length - this.attachmentsToDelete.length).toString(),
      );
      fd.append('attachments_to_delete', this.attachmentsToDelete.join(','));

      this.rfqService.updateRfq(this.rfq.id, fd).subscribe(
        (resp :any) => {
          this.uploadFiles(resp.record, true);
        },
        (errorResp :any) => {
          if (
            errorResp.error?.error_type &&
            errorResp.error?.error_type === 'Existing'
          ) {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: errorResp.error.message,
              type: 'error',
              showCancelButton: true,
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
              cancelButtonText: 'View SKU',
            }).then((result) => {
              this.loading = false;
              if (result.value) {
                this.cd.markForCheck();
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                window.open(
                  `inventory/skus?sku=${data.internal_sku}&discontinued=2&disable_grouping=true`,
                  '_blank',
                );
              }
            });
          } else {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
              type: 'error',
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
            }).then((data) => {
              this.loading = false;
              this.cd.markForCheck();
            });
          }
        },
      );
    } else {
      this.rfqService.createRfq(fd).subscribe(
        (resp :any) => {
          this.uploadFiles(resp.record, false);
        },
        (errorResp :any) => {
          if (
            errorResp.error?.error_type &&
            errorResp.error?.error_type === 'Existing'
          ) {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: errorResp.error.message,
              type: 'error',
              showCancelButton: true,
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
              cancelButtonText: 'View SKU',
            }).then((result) => {
              this.loading = false;
              if (result.value) {
                this.cd.markForCheck();
              } else if (result.dismiss === Swal.DismissReason.cancel) {
                window.open(
                  `inventory/skus?sku=${data.internal_sku}&discontinued=2&disable_grouping=true`,
                  '_blank',
                );
              }
            });
          } else {
            Swal.fire({
              title: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TITLE'),
              text: this.translate.instant('MESSAGES.SERVER_SAVE_ERROR.TEXT'),
              type: 'error',
              confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
            }).then((data) => {
              this.loading = false;
              this.cd.markForCheck();
            });
          }
        },
      );
    }
  }

  // upload files in parallel/async and keep track of the status
  uploadFiles(rfq, isUpdate :boolean) {
    this.uploadedFilesCnt = this.filesToUpload.length;
    const fileremarks = this.quoteForm.getRawValue().fileremarks;

    let uploadedCount = fileremarks.length;
    const uploadErrors :any = [];

    fileremarks.forEach((fileremark, idx) => {
      const fd = new FormData();
      if (this.filesToUpload[idx].file) {
        fd.append('file', this.filesToUpload[idx].file);
        fd.append('remarks', fileremark);
        this.rfqService.uploadAttachments(rfq.id, fd).subscribe(
          (resp :any) => {
            uploadedCount -= 1;
            if (uploadedCount == 0) {
              this.showErroOrCloseForm(uploadErrors, rfq, isUpdate);
            }
          },
          (error) => {
            uploadErrors.push(this.filesToUpload[idx].file);
            uploadedCount -= 1;
            if (uploadedCount == 0) {
              this.showErroOrCloseForm(uploadErrors, rfq, isUpdate);
            }
          },
        );
      } else {
        uploadedCount -= 1;
      }
    });

    if (uploadedCount == 0) {
      this.showErroOrCloseForm(uploadErrors, rfq, isUpdate);
    }
  }

  // check if there are upload error and display the files not uploaded otherwise return created or updated
  showErroOrCloseForm(uploadErrors :any, rfq :any, isUpdate :boolean) {
    if (uploadErrors && uploadErrors.length > 0) {
      // message these rfq is saved but these files were not upload, please upload again.
      let filenames :any = '';
      uploadErrors.forEach((file) => {
        filenames = filenames + '<li>' + file.name + '</li>';
      });
      Swal.fire({
        title: 'File Upload Error',
        html:
          '<h6> These files were not uploaded. Please upload them again manually.' +
          '</h6>' +
          '<ul>' +
          filenames +
          '</ul>',
        type: 'warning',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      }).then(() => {});
    } else {
      Swal.close();
      this.loading = false;
      if (isUpdate) {
        this.toastr.success('Successfully updated client SKU.', 'Success');
      } else {
        this.toastr.success('Successfully created client SKU.', 'Success');
      }
      this.activeModal.close(rfq);
    }
  }

  handleFileInput(event :any) {
    const files = Array.prototype.slice.call(event.target.files);
    const fileremarks = this.quoteForm.get('fileremarks') as UntypedFormArray;
    const kani = this;
    let maxSizeError = false;
    files.forEach((file, idx) => {
      if (file.size > 10000000) {
        maxSizeError = true;
      } else {
        const ftu :any = {
          previewSrc: '',
          file: file,
        };
        this.filesToUpload.push(ftu);
        fileremarks.push(this.fb.control(''));
        if (file.type.toString().search('image') >= 0) {
          const index = this.filesToUpload.length - 1;
          const fReader = new FileReader();
          fReader.readAsDataURL(file);
          fReader.onload = function (e :any) {
            kani.filesToUpload[index].previewSrc = e.target.result;
          };
        }
      }
    });
    if (maxSizeError) {
      Swal.fire({
        title: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TITLE'),
        text: this.translate.instant('MESSAGES.FILE_MAX_SIZE_ERROR.TEXT'),
        type: 'error',
        confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
      });
    }
    event.target.value = '';
  }

  removeFile(idx) {
    const fileremarks = this.quoteForm.get('fileremarks') as UntypedFormArray;
    if (this.rfqAttachments[idx]) {
      this.attachmentsToDelete.push(this.rfqAttachments[idx].filename);
    }
    fileremarks.removeAt(idx);
    this.rfqAttachments.splice(idx, 1);
    this.filesToUpload.splice(idx, 1);
  }

  addVariant(variant ?:any, idx ?:number) {
    const modalRef = this.modalSvc.open(CreateClientRfqVariantComponent, {});

    modalRef.componentInstance.selectedVariants = this.productVariants;

    if (variant) {
      modalRef.componentInstance.variant = variant;
    }

    modalRef.result.then(
      (data) => {
        if (idx >= 0) {
          this.productVariants.splice(idx, 1, data);
        } else {
          this.productVariants.push(data);
        }
        this.cd.detectChanges();
      },
      (_) => {},
    );
  }

  deleteFile(file, idx) {
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete SKU attachment?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.rfq.rfq_attachments.splice(idx, 1);
        this.rfqService
          .deleteAttachment(this.rfq.id, file.id)
          .subscribe((resp :any) => {
            this.toastr.success('Successfully deleted attachment.', 'Success');
          });
      }
    });
  }

  gotoTab(tabNumber :number) {
    this.submitted = true;
    if (tabNumber == 1) {
      if (
        !this.quoteForm.getRawValue().product_name ||
        !this.quoteForm.getRawValue().product_category_id
      ) {
        this.showFormError = true;

        return;
      }
    }
    this.activeTab = tabNumber;
    this.showFormError = false;
    this.submitted = false;
  }

  customTagsSearchFn(term :any, item :any) {
    term = term.toLocaleLowerCase();

    return item.name
      .toLowerCase()
      .replace(/&nbsp;/gi, '')
      .includes(term);
  }

  isRfqCategoryIdExist() :void {
    const categories = this.productCategoriesSvc.productCategories$;
    if (categories.findIndex((x) => x.id === this.rfq.product_category_id) === -1) {
      this.getSubCategories();
    }
  }

  private getIndicesOf(searchStr :string, str :string) :number[] {
    const searchStrLen = searchStr.length;
    if (searchStrLen == 0) {
      return [];
    }

    let startIndex = 0,
      index,
      indices = [];
    while ((index = str.indexOf(searchStr, startIndex)) > -1) {
      indices.push(index);
      startIndex = index + searchStrLen;
    }

    return indices;
  }

  private getIncrementedVariantSku(sku :any) :string {
    if (sku.variant_skus && sku.variant_skus.length > 0) {
      const highestVariantIndex = this.getHighestVariantIndex(sku.variant_skus);
      const internal_sku =
        sku.variant_skus[sku.variant_skus.length - 1].internal_sku;
      const indices = this.getIndicesOf('-', internal_sku);
      const firstHalf = internal_sku.slice(0, indices[indices.length - 1] + 1);
      const secondHalf = internal_sku.slice(indices[indices.length - 1] + 1);
      const increment = highestVariantIndex + 1;

      if (isNaN(increment)) {
        return internal_sku + '-1';
      }

      return firstHalf + increment;
    }

    return sku.internal_sku + '-1';
  }

  private getHighestVariantIndex(variantSkus :any) :number {
    const variantIndexes :number[] = [];
    //Segregate the index of variant
    variantSkus.forEach((data :any) => {
      const variantIndexValue = data.internal_sku.split('-');
      variantIndexes.push(Number(variantIndexValue[1]));
    });

    let max = 0;
    //get the highest variant index value
    variantIndexes.forEach((value :number) => {
      if (value > max) {
        max = value;
      }
    });

    return max;
  }

  getCustomFieldControls() :UntypedFormArray {
    return this.quoteForm['controls'].custom_fields['controls'] as UntypedFormArray;
  }

  getCustomFieldCount() :number {
    return this.getCustomFieldControls()
      ? this.getCustomFieldControls().length - 1
      : 0;
  }

  private loadBaseUnitClass() :void {
    this.unitClassService.getBaseUnit();
  }

  private getDataFromService() :void {
    this.baseUnitClass$ = this.unitClassService.baseUnitClass$;
    this.unitClasses$ = this.unitClassService.unitClasses$;
    this.unitMeasures$ = this.unitMeasureService.unitMeasures$;
    this.generatedSku$ = this.rfqService.generatedSku$;
    this.generatingSku$ = this.rfqService.generatingSku$;
    this.gettingDefaultWarehouseLocations$ =
      this.locationService.gettingDefaultWarehouseLocations$;
    this.defaultWarehouseLocations$ =
      this.locationService.defaultWarehouseLocations$;
  }

  private getBaseUnitClass() :Observable<UnitClassInterface> {
    return this.baseUnitClass$.pipe(
      tap((unitClass) => {
        this.setDefaultUnitClass(unitClass);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private setDefaultUnitClass(unitClass :any) :void {
    this.baseUnitClass = unitClass;
    this.quoteForm.patchValue({
      unit_class_id: unitClass?.id,
    });
    this.loadUnitMeasures();
  }

  setUnitClass() :void {
    this.loadUnitMeasures();

    this.quoteForm.patchValue({
      default_selling_unit_id: null,
    });

    this.quoteForm.patchValue({
      default_purchase_unit_id: null,
    });
  }

  trackUnitClassByFn(item :any) {
    return item.id;
  }

  trackUnitMeasureByFn(item :any) {
    return item.id;
  }

  trackDefaultWarehouseLocationByFn(item :any) :number {
    return item.id;
  }

  private loadUnitClasses() :void {
    this.unitClassService.get({
      ...this.queryParams,
      limit: '999',
    });
  }

  private loadUnitMeasures() :void {
    this.unitMeasureService.get({
      ...this.queryParams,
      limit: '100',
      unitClassId: this.quoteForm.getRawValue().unit_class_id,
    });
  }

  private unitClassInputUpdated() :void {
    concat(
      of([]),
      this.unitClassInput$.asObservable().pipe(
        startWith(''),
        distinctUntilChanged(),
        tap((term) => {
          this.unitClassService.get({
            ...this.queryParams,
            name: term,
            limit: '999',
          });
        }),
      ),
    );
  }

  private unitMeasureInputUpdated() :void {
    concat(
      of([]),
      this.unitMeasureInput$.asObservable().pipe(
        startWith(''),
        distinctUntilChanged(),
        tap((term) => {
          this.unitMeasureService.get({
            ...this.queryParams,
            name: term,
            limit: '100',
            unitClassId: this.quoteForm.getRawValue().unit_class_id,
          });
        }),
      ),
    );
  }

  private defaultWarehouseLocationInputUpdated() :void {
    this.defaultWarehouseLocationInput$
      .pipe(distinctUntilChanged(), debounceTime(800), takeUntil(this.onDestroy$))
      .subscribe((term) => {
        this.locationService.getDefaultWarehouseLocations({
          searchQuery: term,
        });
      });
  }

  private generateSku() :void {
    if (this.rfq || this.parentSku) return;

    this.rfqService.generateSku();
  }

  private autopopulateGeneratedSku() :void {
    if (this.rfq || this.parentSku) return;

    this.generatedSku$
      .pipe(
        distinctUntilChanged(),
        filter((value) => value), // filter not null / truthy
      )
      .subscribe((value) => {
        if (value) {
          this.quoteForm.patchValue({ internal_sku: value });
        }
      });
  }

  private appendWarehouseLocations() :void {
    const locations = [];
    if (this.rfq?.default_receiving_bin) {
      const { location_name, id } = this.rfq?.default_receiving_bin;
      locations.push({ id, locationName: location_name });
    }

    if (this.parentSku?.default_receiving_bin) {
      const { location_name, id } = this.parentSku?.default_receiving_bin;
      locations.push({ id, locationName: location_name });
    }

    if (this.defaultReceivingBin) {
      const { location_name, id } = this.defaultReceivingBin;
      locations.push({ id, locationName: location_name });
    }

    if (locations.length) {
      this.locationService.setDefaultWarehouseLocations(locations);
    }
  }

  get kitItems() :UntypedFormArray {
    return this.quoteForm.get('kitItems') as UntypedFormArray;
  }

  get showKitItems() :boolean {
    return this.quoteForm.getRawValue().type === this.productTypes.KIT;
  }

  get kitItemsCount() :number {
    return this.kitItems.value.filter((item) => item?.productId).length;
  }

  get isKitItemsValid() :boolean {
    return (
      this.quoteForm.getRawValue().type === this.productTypes.KIT &&
      !this.kitItemsCount
    );
  }

  get hasKitItems() :boolean {
    return this.kitItems.value.filter((item) => item.productId).length;
  }

  private checkLotTrackingIfEnabled() :void {
    this.lotTrackingService
      .checkLotTrackingEnabled()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value :boolean) => {
        this.lotTrackingEnabled = value;
      });
  }

  get isKit() :boolean {
    return this.quoteForm.getRawValue().type === this.productTypes.KIT;
  }

  get isKitAndEnabledKitStructure() :boolean {
    return this.isKit;
  }

  onProductTypeChange() :void {
    const unitClassId :UntypedFormControl = this.quoteForm.get(
      'unit_class_id',
    ) as UntypedFormControl;
    const defaultSellingUnitId :UntypedFormControl = this.quoteForm.get(
      'default_selling_unit_id',
    ) as UntypedFormControl;
    const defaultPurchaseUnitId :UntypedFormControl = this.quoteForm.get(
      'default_purchase_unit_id',
    ) as UntypedFormControl;

    if (this.isKitAndEnabledKitStructure) {
      unitClassId.removeValidators(Validators.required);
      defaultSellingUnitId.removeValidators(Validators.required);
      defaultPurchaseUnitId.removeValidators(Validators.required);
    } else {
      unitClassId.addValidators(Validators.required);
      defaultSellingUnitId.addValidators(Validators.required);
      defaultPurchaseUnitId.addValidators(Validators.required);
    }
    this.cd.detectChanges();
  }

  get typeControl() :UntypedFormControl {
    return this.quoteForm.get('type') as UntypedFormControl;
  }

  setProductSubstitutionData(event :any) :void {
    this.productSubstitutions = event;
  }

  onCustomFieldChange(customField :string) :void {
    this.quoteForm.get('customFieldValues').patchValue(customField);
  }

  private bomInputUpdated() :void {
    this.bomLoading = true;
    this.bomInput$
      .pipe(
        startWith(''),
        distinctUntilChanged(),
        debounceTime(800),
        takeUntil(this.onDestroy$),
      )
      .subscribe((term) => {
        this.bomService
          .get({
            name: term,
            limit: '1000',
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(
            (response) => {
              this.boms = response.data;
              this.bomLoading = false;
              this.cd.detectChanges();
            },
            (error) => {
              this.bomLoading = false;
              this.cd.detectChanges();
            },
          );
      });
  }

  private fulfillmentPriorityListInputUpdated() :void {
    this.fulfillmentPriorityListLoading = true;
    this.fulfillmentPriorityListInput$
      .pipe(
        startWith(''),
        distinctUntilChanged(),
        debounceTime(800),
        takeUntil(this.onDestroy$),
      )
      .subscribe((value) => {
        const queryParams = QueryBuilderUtils.buildFilterableModelRequestParams({
          filters: [
            {
              column: 'name',
              operator: 'like',
              value: value,
            },
          ],
        });

        this.fulfillmentPriorityListApiService
          .get(queryParams)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(
            (response) => {
              this.fulfillmentPriorityList = response.data;
              this.fulfillmentPriorityListLoading = false;
              this.cd.detectChanges();
            },
            (error) => {
              this.fulfillmentPriorityListLoading = false;
              this.cd.detectChanges();
            },
          );
      });
  }

  trackBomByFn(item :any) :number {
    return item.id;
  }

  trackById(item :any) :number {
    return item.id;
  }
}
